import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { Logo } from "../components/Brand";
import { API, money } from "../lib/site";

export default function ThankYou() {
  const [params] = useSearchParams();
  const tipId = params.get("tip_id");
  const [state, setState] = useState({ status: tipId ? "CHECKING" : "MISSING" });

  useEffect(() => {
    if (!tipId) return undefined;
    let tries = 0;
    let timer;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await axios.get(`${API}/tips/${tipId}/status`);
        if (data?.status === "COMPLETED") return setState(data);
      } catch (e) {
        if (e?.response?.status === 404) return setState({ status: "MISSING" });
      }
      if (tries < 10) timer = setTimeout(poll, 2500);
      else setState({ status: "PENDING" });
    };
    poll();
    return () => clearTimeout(timer);
  }, [tipId]);

  const done = state.status === "COMPLETED";
  return (
    <main data-testid="thank-you-page" className="min-h-screen grid place-items-center px-5 py-20">
      <motion.div data-testid="thank-you-modal" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}
        className="max-w-xl w-full text-center">
        <div className="flex justify-center"><Logo size={64} animate /></div>
        {state.status === "CHECKING" && (
          <>
            <h1 className="mt-10 font-serif-d text-5xl">Confirming with Square…</h1>
            <Loader2 className="mx-auto mt-8 animate-spin text-[#e3ba73]" />
          </>
        )}
        {done && (
          <>
            <p className="mt-10 overline">{state.frequency === "monthly" ? `Monthly support started · ${money(state.amount)}/month` : `Tip received · ${money(state.amount)}`}</p>
            <h1 data-testid="thank-you-title" className="mt-6 font-serif-d text-5xl sm:text-7xl leading-[0.95]">
              Thank you{state.name ? <>,<br /><em className="gold-foil">{state.name}</em></> : <em className="gold-foil">.</em>}
            </h1>
            <p className="mt-8 text-[#c4bbb0] text-lg leading-relaxed">{state.frequency === "monthly" ? "Becoming a monthly supporter keeps me writing month after month. It genuinely means the world." : "Your support keeps me writing. It genuinely means the world."}<br />— Chris</p>
          </>
        )}
        {state.status === "PENDING" && (
          <>
            <h1 className="mt-10 font-serif-d text-5xl">Still confirming.</h1>
            <p data-testid="thank-you-pending" className="mt-6 text-[#c4bbb0]">Square hasn't confirmed this payment yet. If you completed checkout, it will appear shortly — thank you.</p>
          </>
        )}
        {state.status === "MISSING" && (
          <h1 data-testid="thank-you-missing" className="mt-10 font-serif-d text-4xl">We couldn't find that tip.</h1>
        )}
        <Link to="/" data-testid="thank-you-home-link" className="inline-block mt-12 rounded-full border border-[#e3ba73]/50 text-[#e3ba73] px-7 py-3 hover:bg-[#e3ba73] hover:text-[#121016] transition-colors duration-300">
          Back to the page
        </Link>
      </motion.div>
    </main>
  );
}
